import { formFactorHint, type LayoutOption } from '../presentation/variants';
import './layouts.css';

/** What the game display needs to offer a layout choice (docs/ui-variants-design.md). Absent for a single layout. */
export interface LayoutApi {
  options: LayoutOption[];
  current: string;
  select: (id: string) => void;
}

const labelOf = (api: LayoutApi) => api.options.find(o => o.id === api.current)?.label ?? api.current;

/** Compact header chip showing the current layout; it opens the settings popup that holds the picker. */
export function LayoutChip({ api, popoverId }: { api: LayoutApi; popoverId: string }) {
  return <button type="button" className="game-layout-chip" popoverTarget={popoverId} title={`Layout: ${labelOf(api)}. Change it in the game settings.`} aria-label={`Layout ${labelOf(api)}. Open game settings to change it.`}>
    <span className="game-layout-chip-label">{labelOf(api)}</span>
  </button>;
}

/** The "Layout" select in the settings popup. Every layout shows the same information; only the arrangement differs. */
export function LayoutSelect({ api }: { api: LayoutApi }) {
  const current = api.options.find(o => o.id === api.current);
  return <>
    <label>Layout <select aria-label="Layout" value={api.current} onChange={e => api.select(e.target.value)}>
      {api.options.map(o => <option key={o.id} value={o.id}>{o.label}{formFactorHint(o.formFactor) ? ` · ${formFactorHint(o.formFactor)}` : ''}</option>)}
    </select></label>
    {current?.summary && <p>{current.summary}</p>}
    <p>Layouts are only different views of the same game: they show you exactly the same information.</p>
  </>;
}

/** Dismissible notice for an unknown `?ui=` request. */
export function LayoutNotice({ requested, onDismiss }: { requested: string; onDismiss: () => void }) {
  return <div className="game-layout-notice" role="status">
    <span>Layout ‘{requested}’ isn't available for this game; showing the default.</span>
    <button type="button" onClick={onDismiss} aria-label="Dismiss layout notice">×</button>
  </div>;
}
